/**
 * Referrals -- amounts, promotion links, live counts (phase 6b, Task 6).
 *
 * One config row (`GET/PUT /referrals/config`): the referee discount and
 * referrer reward amounts, resolved through two seeded singleton promotions
 * (`referralPromotion` kind REFERRAL, `rewardPromotion` kind REFERRAL_REWARD
 * -- ruling P6-1, ADMIN_API.md). Both amounts are required and must each be
 * > 0; the backend enforces that with a whole-body `.refine` (path '' --
 * `schemas/referrals.ts`), so a 422 lands as one message rather than a
 * per-field one -- shown as a form-level alert here, not attached to either
 * input.
 *
 * Read-only for `staff` (`useCanWriteDiscounts()`); `admin`/`owner` can edit
 * and save.
 *
 * `toApiFailure` (errors.ts) skips any `errors[]` entry whose `path` is `''`
 * when building `fieldErrors` (`e?.path && ...` -- an empty string is
 * falsy), by design: a whole-body issue "lands only in `message`". So the
 * referrals 422 is never attachable to either field -- it is shown as a
 * form-level alert built from `failure.message`, not from `fieldErrors`.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Link,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { formatRand, toMoneyString } from '../money';
import { useCanWriteDiscounts } from '../roles';
import type { ReferralConfig, ReferralConfigBody } from '../types';

const promotionUrl = (id: string): string => `/discounts/promotions/${id}`;

const PromotionLink: React.FC<{ label: string; promotion: ReferralConfig['referralPromotion'] }> = ({
  label,
  promotion,
}) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
      {label}
    </Typography>
    <Link component={RouterLink} to={promotionUrl(promotion.id)} underline="hover">
      {promotion.name}
    </Link>
    <Chip
      size="small"
      label={promotion.active ? 'Active' : 'Inactive'}
      color={promotion.active ? 'success' : 'default'}
      variant={promotion.active ? 'filled' : 'outlined'}
    />
  </Stack>
);

const Counts: React.FC<{ config: ReferralConfig }> = ({ config }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
    {[
      { label: 'Codes issued', value: config.codesIssued },
      { label: 'Converted', value: config.converted },
      { label: 'Reverted', value: config.reverted },
    ].map(({ label, value }) => (
      <Box key={label}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="h6" fontWeight={700}>
          {value}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const ReferralsScreen: React.FC = () => {
  const canWrite = useCanWriteDiscounts();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({ queryKey: qk.referralConfig, queryFn: discountsApi.referralConfig });

  const [refereeInput, setRefereeInput] = React.useState('');
  const [referrerInput, setReferrerInput] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);

  // Reset the editable fields whenever a fresh config loads -- initial fetch,
  // or the refetch after a successful save -- keyed on updatedAt so an
  // unrelated background refetch (e.g. window refocus) with the same row
  // never clobbers an in-progress edit.
  React.useEffect(() => {
    if (data) {
      setRefereeInput(data.refereeDiscount);
      setReferrerInput(data.referrerReward);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.updatedAt]);

  const mutation = useMutation({
    mutationFn: (body: ReferralConfigBody) => discountsApi.putReferralConfig(body),
    onSuccess: (updated) => {
      queryClient.setQueryData(qk.referralConfig, updated);
      queryClient.invalidateQueries({ queryKey: qk.referralConfig });
      queryClient.invalidateQueries({ queryKey: [...qk.all, 'promotions'] });
      setFieldErrors({});
      setFormError(null);
      notify('Referral amounts saved', { type: 'success' });
    },
    onError: (err) => {
      const failure = toApiFailure(err);
      if (failure.status === 422) {
        // Whole-body refine (path ''): the form-level Alert is the one place it
        // shows -- no snackbar duplicating the same copy (fix wave minor 11).
        setFieldErrors(failure.fieldErrors);
        setFormError(failure.message);
        return;
      }
      setFieldErrors({});
      setFormError(null);
      notify(failure.message, { type: 'error' });
    },
  });

  const handleSave = () => {
    const errs: Record<string, string> = {};
    let refereeDiscount: string | null = null;
    let referrerReward: string | null = null;
    try {
      refereeDiscount = toMoneyString(refereeInput);
      if (refereeDiscount == null) errs.refereeDiscount = 'Required.';
    } catch (e) {
      errs.refereeDiscount = (e as Error).message;
    }
    try {
      referrerReward = toMoneyString(referrerInput);
      if (referrerReward == null) errs.referrerReward = 'Required.';
    } catch (e) {
      errs.referrerReward = (e as Error).message;
    }
    setFieldErrors(errs);
    setFormError(null);
    if (Object.keys(errs).length > 0 || refereeDiscount == null || referrerReward == null) return;
    mutation.mutate({ refereeDiscount, referrerReward });
  };

  if (error) {
    return (
      <Alert severity="error" data-testid="referrals-error">
        Could not load referral config: {toApiFailure(error).message}
      </Alert>
    );
  }

  if (isLoading || !data) {
    return (
      <Card variant="outlined" sx={{ p: 3 }}>
        <Skeleton width={200} height={32} />
        <Skeleton width="100%" height={56} sx={{ mt: 2 }} />
        <Skeleton width="100%" height={56} sx={{ mt: 1 }} />
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Referral amounts
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <PromotionLink label="Referee discount promotion" promotion={data.referralPromotion} />
        <PromotionLink label="Referrer reward promotion" promotion={data.rewardPromotion} />
      </Stack>

      {formError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="referrals-form-error">
          {formError}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 3 }}>
        {canWrite ? (
          <TextField
            label="Referee discount (R)"
            value={refereeInput}
            onChange={(e) => setRefereeInput(e.target.value)}
            error={Boolean(fieldErrors.refereeDiscount)}
            helperText={fieldErrors.refereeDiscount ?? 'Applied to the referred customer’s first order.'}
            disabled={mutation.isPending}
            fullWidth
          />
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Referee discount
            </Typography>
            <Typography variant="body1">{formatRand(data.refereeDiscount)}</Typography>
          </Box>
        )}

        {canWrite ? (
          <TextField
            label="Referrer reward (R)"
            value={referrerInput}
            onChange={(e) => setReferrerInput(e.target.value)}
            error={Boolean(fieldErrors.referrerReward)}
            helperText={fieldErrors.referrerReward ?? 'Issued as a voucher to the referrer once the referral converts.'}
            disabled={mutation.isPending}
            fullWidth
          />
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Referrer reward
            </Typography>
            <Typography variant="body1">{formatRand(data.referrerReward)}</Typography>
          </Box>
        )}
      </Stack>

      {canWrite && (
        <Button variant="contained" onClick={handleSave} disabled={mutation.isPending}>
          Save
        </Button>
      )}

      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Counts config={data} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Last updated {new Date(data.updatedAt).toLocaleString()}
          {data.updatedBy ? ` by ${data.updatedBy}` : ''}
        </Typography>
      </Box>
    </Card>
  );
};

export default ReferralsScreen;
